import { useMemo } from 'react'

import { useAccount } from 'wagmi'
import { AirdropItem } from 'types/entities'

import { useAirdropState } from 'hooks/airdrop'
import { useNativeCurrency } from 'hooks/token'
import { useGetBalancesRequest } from 'hooks/api'

import {
  useToken,
  useTxsList,
  useBatchSize,
  useAirdropList,
  useTotalTokens,
  useTransactionsCount,
} from 'state/airdrop/hooks'

import { fromWei } from 'utils/crypto'
import { sliceValue } from 'utils/helpers'

import { Icon } from 'components/ui'

import { Statuses, Stat, StatsRecord } from '../@types'
import { Wrap, Item, ItemIcon, ItemInfo, ItemTitle, ItemValue, Divider } from './Style'

type Props = {
  layout?: string
  status: Statuses[]
}

const getUniqRecipients = (acc: string[], curr: AirdropItem) => {
  if (acc.includes(curr.addr)) {
    return acc
  }
  acc.push(curr.addr)
  return acc
}

export function Stats({ status, layout }: Props) {
  const token = useToken()
  const { address } = useAccount()
  const batchSize = useBatchSize()
  const transactionsCount = useTransactionsCount()

  const txs = useTxsList()

  const airdropState = useAirdropState()
  const totalTokensWei = useTotalTokens()
  const nativeCurrency = useNativeCurrency()
  const getBalancesRequest = useGetBalancesRequest()

  const airdropList = useAirdropList()

  const filteredStats = (status: Statuses[]): Stat[] => {
    return status.reduce((acc: Stat[], curr: Statuses) => {
      const stat: Stat = stats[curr]
      if (stat) {
        acc.push(stat)
      }
      return acc
    }, [])
  }

  const totalRecipients = useMemo(() => {
    return airdropList.reduce<string[]>(getUniqRecipients, []).length
  }, [airdropList])

  const batchInfo = useMemo(() => {
    return { batchesCount: transactionsCount, batchSize: Number(batchSize) }
  }, [transactionsCount, batchSize])

  const customerBalances = useMemo(
    () => (address ? getBalancesRequest?.data?.[address] : null),
    [getBalancesRequest?.data, address],
  )

  const tokenBalance = useMemo(() => {
    if (token) {
      const balances = customerBalances?.[token.address]?.balance

      if (balances) {
        return sliceValue(balances)
      }
      return '0'
    }
    return '0'
  }, [customerBalances, token])

  const totalTokens = useMemo(() => {
    return fromWei(totalTokensWei.toString(), token?.decimals)
  }, [totalTokensWei, token])

  const ethBalance = useMemo(() => {
    return sliceValue(customerBalances?.[nativeCurrency.address].balance)
  }, [customerBalances, nativeCurrency.address])

  const _approximateCost = useMemo(
    () => (txs.length > 0 ? sliceValue(airdropState.approximateCostToDisplay.toString()) : '...loading'),
    [airdropState.approximateCostToDisplay, txs],
  )

  const stats: StatsRecord = useMemo(
    () => ({
      [Statuses.TOTAL_TOKENS_TO_SEND]: {
        label: 'Total-tokens-to-be-sent',
        data: `${totalTokens} ${token?.symbol}`,
      },
      [Statuses.TOKEN_BALANCE]: { label: 'Your-token-balance', data: `${tokenBalance} ${token?.symbol}` },
      [Statuses.APPROXIMATE_COST]: { label: 'Approximate-cost', data: `${_approximateCost} ${nativeCurrency.symbol}` },
      [Statuses.TOTAL_ADDRESSES]: { label: 'Total-number-of-addresses', data: totalRecipients },
      [Statuses.TOTAL_TXS]: { label: 'Total-transactions-required', data: batchInfo.batchesCount },
      [Statuses.NATIVE_CURR]: {
        label: `Your-${nativeCurrency.symbol}-balance`,
        data: `${ethBalance} ${nativeCurrency.symbol}`,
      },
    }),
    [totalTokens, token, tokenBalance, _approximateCost, totalRecipients, batchInfo, ethBalance, nativeCurrency],
  )

  const filteredData = useMemo(() => filteredStats(status), [stats, status])

  return (
    <Wrap layout={layout}>
      {filteredData.map((item, index) => (
        <Item key={index}>
          <ItemIcon>
            <Icon name='hashtag' />
          </ItemIcon>
          <ItemInfo>
            <ItemTitle>{item.label}</ItemTitle>
            <Divider>==&gt;</Divider>
            <ItemValue>{item.data}</ItemValue>
          </ItemInfo>
        </Item>
      ))}
    </Wrap>
  )
}
